// Code generated by protoc-gen-ts_proto. DO NOT EDIT.
// versions:
//   protoc-gen-ts_proto  v2.6.1
//   protoc               unknown
// source: models/user.proto

/* eslint-disable */
import { BinaryReader, BinaryWriter } from "@bufbuild/protobuf/wire";
import { Timestamp } from "../google/protobuf/timestamp";
import {
  C3Context,
  c3ContextFromJSON,
  c3ContextToJSON,
  Theme,
  themeFromJSON,
  themeToJSON,
  TimeFormat,
  timeFormatFromJSON,
  timeFormatToJSON,
  UserRole,
  userRoleFromJSON,
  userRoleToJSON,
} from "./auth";
import { AdminState, adminStateFromJSON, adminStateToJSON } from "./global";
import { Tenant } from "./tenant";
import { Metadata } from "./types";

export const protobufPackage = "models";

/** SignUpRequirement specifies what sign-up steps a user must complete after login. */
export enum SignUpRequirement {
  /** SIGN_UP_REQUIREMENT_UNSPECIFIED - No sign-up required. */
  SIGN_UP_REQUIREMENT_UNSPECIFIED = 0,
  /** SIGN_UP_NEW_ORG - Full sign-up: create new org + sign EULA. */
  SIGN_UP_NEW_ORG = 1,
  /** SIGN_UP_EULA_ONLY - Sign EULA only (federated or invited user). */
  SIGN_UP_EULA_ONLY = 2,
  UNRECOGNIZED = -1,
}

export function signUpRequirementFromJSON(object: any): SignUpRequirement {
  switch (object) {
    case 0:
    case "SIGN_UP_REQUIREMENT_UNSPECIFIED":
      return SignUpRequirement.SIGN_UP_REQUIREMENT_UNSPECIFIED;
    case 1:
    case "SIGN_UP_NEW_ORG":
      return SignUpRequirement.SIGN_UP_NEW_ORG;
    case 2:
    case "SIGN_UP_EULA_ONLY":
      return SignUpRequirement.SIGN_UP_EULA_ONLY;
    case -1:
    case "UNRECOGNIZED":
    default:
      return SignUpRequirement.UNRECOGNIZED;
  }
}

export function signUpRequirementToJSON(object: SignUpRequirement): string {
  switch (object) {
    case SignUpRequirement.SIGN_UP_REQUIREMENT_UNSPECIFIED:
      return "SIGN_UP_REQUIREMENT_UNSPECIFIED";
    case SignUpRequirement.SIGN_UP_NEW_ORG:
      return "SIGN_UP_NEW_ORG";
    case SignUpRequirement.SIGN_UP_EULA_ONLY:
      return "SIGN_UP_EULA_ONLY";
    case SignUpRequirement.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

/** SignUp captures captures information about the company and the individual signing up. */
export interface SignUp {
  /** Name of the company.  The name is unique and case-insensitive. */
  companyName: string;
  /**
   * Email of the individual signing up (required).  Should be the business email
   * (email contains the domain name of the employer of the individual).
   */
  email: string;
  /**
   * Version of the EULA accepted.  A valid value EULA version indicates the user accepted the EULA
   * (a check box on the sign-up page).  Empty means the EULA was not accepted.
   */
  eulaVersion: string;
}

/**
 * A User is a Cisco.com account, identified by email, authorized to access a specific Cisco Nexus Hyperfabric Organization
 * with a specific role that represents the level of privilege of the user.
 * Only a user with administrative privileges may modify another user's user record.
 *
 * INTERNAL
 *
 * User describes a user in a tenant.  A user, identified by email, may belong to multiple tenants.
 * Each instance of a user in a tenant is represented by a separate User record.
 */
export interface User {
  /**
   * The unique identifier of a user.
   *
   * INTERNAL
   *
   * Identifier is required to update an existing User.
   */
  id: string;
  /**
   * The email address of the user.
   *
   * INTERNAL
   *
   * The canonical username of User, must be a valid email address.  Caller must be an administrator
   * and provide the email of a User in the tenant of the given context in order to update that user.
   */
  email: string;
  /** The single sign on (SSO) identity provider (IDP), optional (default: CISCO). */
  provider: string;
  /** A list of user-defined labels that can be used for grouping and filtering users. */
  labels: string[];
  /** The enabled state of the user which indicates if the user is enabled or disabled. */
  enabled: boolean;
  /** A map of attributes related to the lifecycle of the user. */
  metadata:
    | Metadata
    | undefined;
  /** The role assigned to the user that represents the level of privilege of the user. */
  role: UserRole;
  /**
   * The last recorded web interface login time of the user in
   * [RFC3339](https://datatracker.ietf.org/doc/html/rfc3339#section-5.8) format
   * (e.g. `YYYY-MM-DDTHH:MM:SSZ`).
   */
  lastLogin:
    | Date
    | undefined;
  /** Internal field. */
  userType: string;
  /** Corresponding CUI user ID. */
  cuiUserId: string;
  /** The administrative state of the user (e.g. ENABLED, DISABLED, PENDING). */
  adminState: AdminState;
}

/** UserPreferences describes preferences for a user across all tenants. */
export interface UserPreferences {
  /** The single sign on (SSO) identity provider (IDP), optional (default: CISCO). */
  provider: string;
  /** Preferred timezone. */
  timezone: string;
  /** Users preferred theme. */
  theme: Theme;
  /** Indicates whether to skip the video tour or not. */
  skipVideoTour: boolean;
  /** Indicates whether to skip the feature highlights or not. */
  skipFeatureHighlights: boolean;
  /** User selected timeFormat */
  timeFormat: TimeFormat;
}

/** UserContext describes a context in which the user is a member. */
export interface UserContext {
  /** Identifies the organization of which tenant belongs. */
  orgId: string;
  /** The name of the organization of which tenant belongs. */
  orgName: string;
  /** Current indicates this is the context of the user receiving this information. */
  current: boolean;
  /** Tenant in which user is a member. */
  tenant:
    | Tenant
    | undefined;
  /** Information about user in the context of tenant. */
  user: User | undefined;
}

export interface Eula {
  email: string;
  eulaVersion: string;
}

export interface GetCreateOrgPermissionsRequest {
  email: string;
}

export interface GetCreateOrgPermissionsResponse {
  allowed: boolean;
}

export interface UpdateUserPreferencesRequest {
  email: string;
  provider: string;
  userPreferences: UserPreferences | undefined;
}

/** UserSessionResponse includes information about the user of the current session. */
export interface UserSessionResponse {
  /** Identifies the organization of the session to which tenant belongs.  Empty if new user. */
  orgId: string;
  /** The name of the organization of the session to which tenant belongs.  Empty if new user. */
  orgName: string;
  /**
   * Identifies the tenant of the session to which the user belongs.  If tenant_id equals org_id,
   * then the session is in the context of the default-tenant of this organization.  Sessions
   * from default-tenants have different privileges than sessions from non-default tenants.
   * Empty if new user.
   */
  tenantId: string;
  /** The name of the tenant of the session to which the user belongs.  Empty if new user. */
  tenantName: string;
  /** Email of the authenticated user.  Globally unique. */
  email: string;
  /** The single sign on (SSO) identity provider (IDP). */
  provider: string;
  /** Full name of user from identity provider. */
  name: string;
  /** Is true if this is a new user (not associated with any organization and tenant). */
  newUser: boolean;
  /** Expiration timestamp of this user session. */
  expiry:
    | Date
    | undefined;
  /** Last login time */
  loginTime:
    | Date
    | undefined;
  /** This user is required to sign up if true. */
  requireSignUp: boolean;
  /** Identifies user's session */
  sessionId: string;
  /** User UUID. */
  userId: string;
  /** The role assigned to the user that represents the level of privilege of the user. */
  role: UserRole;
  /** A list of user-defined labels that can be used for grouping and filtering users. */
  labels: string[];
  /** CUI tenant id associated with the user session's Hyperfabric org. */
  cuiTenantId: string;
  /** Is true if this is a federated user. */
  federatedUser: boolean;
  /** UI version (git commit hash) */
  uiVersion: string;
  /** UI deployment timestamp in RFC3339 format */
  uiDeploymentTimestamp: string;
  /** Is true if this is a C3 crosslaunch session. */
  isC3Crosslaunch: boolean;
  /** The context of the C3 caller */
  c3Context: C3Context;
  /** Specifies what sign-up steps the user must complete after login. */
  signUpRequirement: SignUpRequirement;
  /** If true the user is allowed to access developer UI. */
  developer: boolean;
}

function createBaseSignUp(): SignUp {
  return { companyName: "", email: "", eulaVersion: "" };
}

export const SignUp: MessageFns<SignUp> = {
  encode(message: SignUp, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.companyName !== "") {
      writer.uint32(10).string(message.companyName);
    }
    if (message.email !== "") {
      writer.uint32(18).string(message.email);
    }
    if (message.eulaVersion !== "") {
      writer.uint32(82).string(message.eulaVersion);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): SignUp {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSignUp();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.companyName = reader.string();
          continue;
        }
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.email = reader.string();
          continue;
        }
        case 10: {
          if (tag !== 82) {
            break;
          }

          message.eulaVersion = reader.string();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): SignUp {
    return {
      companyName: isSet(object.companyName) ? globalThis.String(object.companyName) : "",
      email: isSet(object.email) ? globalThis.String(object.email) : "",
      eulaVersion: isSet(object.eulaVersion) ? globalThis.String(object.eulaVersion) : "",
    };
  },

  toJSON(message: SignUp): unknown {
    const obj: any = {};
    if (message.companyName !== "") {
      obj.companyName = message.companyName;
    }
    if (message.email !== "") {
      obj.email = message.email;
    }
    if (message.eulaVersion !== "") {
      obj.eulaVersion = message.eulaVersion;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SignUp>, I>>(base?: I): SignUp {
    return SignUp.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SignUp>, I>>(object: I): SignUp {
    const message = createBaseSignUp();
    message.companyName = object.companyName ?? "";
    message.email = object.email ?? "";
    message.eulaVersion = object.eulaVersion ?? "";
    return message;
  },
};

function createBaseUser(): User {
  return {
    id: "",
    email: "",
    provider: "",
    labels: [],
    enabled: false,
    metadata: undefined,
    role: 0,
    lastLogin: undefined,
    userType: "",
    cuiUserId: "",
    adminState: 0,
  };
}

export const User: MessageFns<User> = {
  encode(message: User, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.id !== "") {
      writer.uint32(10).string(message.id);
    }
    if (message.email !== "") {
      writer.uint32(18).string(message.email);
    }
    if (message.provider !== "") {
      writer.uint32(26).string(message.provider);
    }
    for (const v of message.labels) {
      writer.uint32(42).string(v!);
    }
    if (message.enabled !== false) {
      writer.uint32(56).bool(message.enabled);
    }
    if (message.metadata !== undefined) {
      Metadata.encode(message.metadata, writer.uint32(66).fork()).join();
    }
    if (message.role !== 0) {
      writer.uint32(88).int32(message.role);
    }
    if (message.lastLogin !== undefined) {
      Timestamp.encode(toTimestamp(message.lastLogin), writer.uint32(98).fork()).join();
    }
    if (message.userType !== "") {
      writer.uint32(106).string(message.userType);
    }
    if (message.cuiUserId !== "") {
      writer.uint32(114).string(message.cuiUserId);
    }
    if (message.adminState !== 0) {
      writer.uint32(120).int32(message.adminState);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): User {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseUser();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.id = reader.string();
          continue;
        }
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.email = reader.string();
          continue;
        }
        case 3: {
          if (tag !== 26) {
            break;
          }

          message.provider = reader.string();
          continue;
        }
        case 5: {
          if (tag !== 42) {
            break;
          }

          message.labels.push(reader.string());
          continue;
        }
        case 7: {
          if (tag !== 56) {
            break;
          }

          message.enabled = reader.bool();
          continue;
        }
        case 8: {
          if (tag !== 66) {
            break;
          }

          message.metadata = Metadata.decode(reader, reader.uint32());
          continue;
        }
        case 11: {
          if (tag !== 88) {
            break;
          }

          message.role = reader.int32() as any;
          continue;
        }
        case 12: {
          if (tag !== 98) {
            break;
          }

          message.lastLogin = fromTimestamp(Timestamp.decode(reader, reader.uint32()));
          continue;
        }
        case 13: {
          if (tag !== 106) {
            break;
          }

          message.userType = reader.string();
          continue;
        }
        case 14: {
          if (tag !== 114) {
            break;
          }

          message.cuiUserId = reader.string();
          continue;
        }
        case 15: {
          if (tag !== 120) {
            break;
          }

          message.adminState = reader.int32() as any;
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): User {
    return {
      id: isSet(object.id) ? globalThis.String(object.id) : "",
      email: isSet(object.email) ? globalThis.String(object.email) : "",
      provider: isSet(object.provider) ? globalThis.String(object.provider) : "",
      labels: globalThis.Array.isArray(object?.labels) ? object.labels.map((e: any) => globalThis.String(e)) : [],
      enabled: isSet(object.enabled) ? globalThis.Boolean(object.enabled) : false,
      metadata: isSet(object.metadata) ? Metadata.fromJSON(object.metadata) : undefined,
      role: isSet(object.role) ? userRoleFromJSON(object.role) : 0,
      lastLogin: isSet(object.lastLogin) ? fromJsonTimestamp(object.lastLogin) : undefined,
      userType: isSet(object.userType) ? globalThis.String(object.userType) : "",
      cuiUserId: isSet(object.cuiUserId) ? globalThis.String(object.cuiUserId) : "",
      adminState: isSet(object.adminState) ? adminStateFromJSON(object.adminState) : 0,
    };
  },

  toJSON(message: User): unknown {
    const obj: any = {};
    if (message.id !== "") {
      obj.id = message.id;
    }
    if (message.email !== "") {
      obj.email = message.email;
    }
    if (message.provider !== "") {
      obj.provider = message.provider;
    }
    if (message.labels?.length) {
      obj.labels = message.labels;
    }
    if (message.enabled !== false) {
      obj.enabled = message.enabled;
    }
    if (message.metadata !== undefined) {
      obj.metadata = Metadata.toJSON(message.metadata);
    }
    if (message.role !== 0) {
      obj.role = userRoleToJSON(message.role);
    }
    if (message.lastLogin !== undefined) {
      obj.lastLogin = message.lastLogin.toISOString();
    }
    if (message.userType !== "") {
      obj.userType = message.userType;
    }
    if (message.cuiUserId !== "") {
      obj.cuiUserId = message.cuiUserId;
    }
    if (message.adminState !== 0) {
      obj.adminState = adminStateToJSON(message.adminState);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<User>, I>>(base?: I): User {
    return User.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<User>, I>>(object: I): User {
    const message = createBaseUser();
    message.id = object.id ?? "";
    message.email = object.email ?? "";
    message.provider = object.provider ?? "";
    message.labels = object.labels?.map((e) => e) || [];
    message.enabled = object.enabled ?? false;
    message.metadata = (object.metadata !== undefined && object.metadata !== null)
      ? Metadata.fromPartial(object.metadata)
      : undefined;
    message.role = object.role ?? 0;
    message.lastLogin = object.lastLogin ?? undefined;
    message.userType = object.userType ?? "";
    message.cuiUserId = object.cuiUserId ?? "";
    message.adminState = object.adminState ?? 0;
    return message;
  },
};

function createBaseUserPreferences(): UserPreferences {
  return { provider: "", timezone: "", theme: 0, skipVideoTour: false, skipFeatureHighlights: false, timeFormat: 0 };
}

export const UserPreferences: MessageFns<UserPreferences> = {
  encode(message: UserPreferences, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.provider !== "") {
      writer.uint32(18).string(message.provider);
    }
    if (message.timezone !== "") {
      writer.uint32(90).string(message.timezone);
    }
    if (message.theme !== 0) {
      writer.uint32(104).int32(message.theme);
    }
    if (message.skipVideoTour !== false) {
      writer.uint32(120).bool(message.skipVideoTour);
    }
    if (message.skipFeatureHighlights !== false) {
      writer.uint32(128).bool(message.skipFeatureHighlights);
    }
    if (message.timeFormat !== 0) {
      writer.uint32(136).int32(message.timeFormat);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): UserPreferences {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseUserPreferences();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.provider = reader.string();
          continue;
        }
        case 11: {
          if (tag !== 90) {
            break;
          }

          message.timezone = reader.string();
          continue;
        }
        case 13: {
          if (tag !== 104) {
            break;
          }

          message.theme = reader.int32() as any;
          continue;
        }
        case 15: {
          if (tag !== 120) {
            break;
          }

          message.skipVideoTour = reader.bool();
          continue;
        }
        case 16: {
          if (tag !== 128) {
            break;
          }

          message.skipFeatureHighlights = reader.bool();
          continue;
        }
        case 17: {
          if (tag !== 136) {
            break;
          }

          message.timeFormat = reader.int32() as any;
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): UserPreferences {
    return {
      provider: isSet(object.provider) ? globalThis.String(object.provider) : "",
      timezone: isSet(object.timezone) ? globalThis.String(object.timezone) : "",
      theme: isSet(object.theme) ? themeFromJSON(object.theme) : 0,
      skipVideoTour: isSet(object.skipVideoTour) ? globalThis.Boolean(object.skipVideoTour) : false,
      skipFeatureHighlights: isSet(object.skipFeatureHighlights)
        ? globalThis.Boolean(object.skipFeatureHighlights)
        : false,
      timeFormat: isSet(object.timeFormat) ? timeFormatFromJSON(object.timeFormat) : 0,
    };
  },

  toJSON(message: UserPreferences): unknown {
    const obj: any = {};
    if (message.provider !== "") {
      obj.provider = message.provider;
    }
    if (message.timezone !== "") {
      obj.timezone = message.timezone;
    }
    if (message.theme !== 0) {
      obj.theme = themeToJSON(message.theme);
    }
    if (message.skipVideoTour !== false) {
      obj.skipVideoTour = message.skipVideoTour;
    }
    if (message.skipFeatureHighlights !== false) {
      obj.skipFeatureHighlights = message.skipFeatureHighlights;
    }
    if (message.timeFormat !== 0) {
      obj.timeFormat = timeFormatToJSON(message.timeFormat);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<UserPreferences>, I>>(base?: I): UserPreferences {
    return UserPreferences.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<UserPreferences>, I>>(object: I): UserPreferences {
    const message = createBaseUserPreferences();
    message.provider = object.provider ?? "";
    message.timezone = object.timezone ?? "";
    message.theme = object.theme ?? 0;
    message.skipVideoTour = object.skipVideoTour ?? false;
    message.skipFeatureHighlights = object.skipFeatureHighlights ?? false;
    message.timeFormat = object.timeFormat ?? 0;
    return message;
  },
};

function createBaseUserContext(): UserContext {
  return { orgId: "", orgName: "", current: false, tenant: undefined, user: undefined };
}

export const UserContext: MessageFns<UserContext> = {
  encode(message: UserContext, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.orgId !== "") {
      writer.uint32(10).string(message.orgId);
    }
    if (message.orgName !== "") {
      writer.uint32(18).string(message.orgName);
    }
    if (message.current !== false) {
      writer.uint32(24).bool(message.current);
    }
    if (message.tenant !== undefined) {
      Tenant.encode(message.tenant, writer.uint32(90).fork()).join();
    }
    if (message.user !== undefined) {
      User.encode(message.user, writer.uint32(170).fork()).join();
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): UserContext {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseUserContext();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.orgId = reader.string();
          continue;
        }
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.orgName = reader.string();
          continue;
        }
        case 3: {
          if (tag !== 24) {
            break;
          }

          message.current = reader.bool();
          continue;
        }
        case 11: {
          if (tag !== 90) {
            break;
          }

          message.tenant = Tenant.decode(reader, reader.uint32());
          continue;
        }
        case 21: {
          if (tag !== 170) {
            break;
          }

          message.user = User.decode(reader, reader.uint32());
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): UserContext {
    return {
      orgId: isSet(object.orgId) ? globalThis.String(object.orgId) : "",
      orgName: isSet(object.orgName) ? globalThis.String(object.orgName) : "",
      current: isSet(object.current) ? globalThis.Boolean(object.current) : false,
      tenant: isSet(object.tenant) ? Tenant.fromJSON(object.tenant) : undefined,
      user: isSet(object.user) ? User.fromJSON(object.user) : undefined,
    };
  },

  toJSON(message: UserContext): unknown {
    const obj: any = {};
    if (message.orgId !== "") {
      obj.orgId = message.orgId;
    }
    if (message.orgName !== "") {
      obj.orgName = message.orgName;
    }
    if (message.current !== false) {
      obj.current = message.current;
    }
    if (message.tenant !== undefined) {
      obj.tenant = Tenant.toJSON(message.tenant);
    }
    if (message.user !== undefined) {
      obj.user = User.toJSON(message.user);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<UserContext>, I>>(base?: I): UserContext {
    return UserContext.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<UserContext>, I>>(object: I): UserContext {
    const message = createBaseUserContext();
    message.orgId = object.orgId ?? "";
    message.orgName = object.orgName ?? "";
    message.current = object.current ?? false;
    message.tenant = (object.tenant !== undefined && object.tenant !== null)
      ? Tenant.fromPartial(object.tenant)
      : undefined;
    message.user = (object.user !== undefined && object.user !== null) ? User.fromPartial(object.user) : undefined;
    return message;
  },
};

function createBaseEula(): Eula {
  return { email: "", eulaVersion: "" };
}

export const Eula: MessageFns<Eula> = {
  encode(message: Eula, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.email !== "") {
      writer.uint32(10).string(message.email);
    }
    if (message.eulaVersion !== "") {
      writer.uint32(18).string(message.eulaVersion);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): Eula {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseEula();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.email = reader.string();
          continue;
        }
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.eulaVersion = reader.string();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): Eula {
    return {
      email: isSet(object.email) ? globalThis.String(object.email) : "",
      eulaVersion: isSet(object.eulaVersion) ? globalThis.String(object.eulaVersion) : "",
    };
  },

  toJSON(message: Eula): unknown {
    const obj: any = {};
    if (message.email !== "") {
      obj.email = message.email;
    }
    if (message.eulaVersion !== "") {
      obj.eulaVersion = message.eulaVersion;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<Eula>, I>>(base?: I): Eula {
    return Eula.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<Eula>, I>>(object: I): Eula {
    const message = createBaseEula();
    message.email = object.email ?? "";
    message.eulaVersion = object.eulaVersion ?? "";
    return message;
  },
};

function createBaseGetCreateOrgPermissionsRequest(): GetCreateOrgPermissionsRequest {
  return { email: "" };
}

export const GetCreateOrgPermissionsRequest: MessageFns<GetCreateOrgPermissionsRequest> = {
  encode(message: GetCreateOrgPermissionsRequest, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.email !== "") {
      writer.uint32(10).string(message.email);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): GetCreateOrgPermissionsRequest {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseGetCreateOrgPermissionsRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.email = reader.string();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): GetCreateOrgPermissionsRequest {
    return { email: isSet(object.email) ? globalThis.String(object.email) : "" };
  },

  toJSON(message: GetCreateOrgPermissionsRequest): unknown {
    const obj: any = {};
    if (message.email !== "") {
      obj.email = message.email;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<GetCreateOrgPermissionsRequest>, I>>(base?: I): GetCreateOrgPermissionsRequest {
    return GetCreateOrgPermissionsRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<GetCreateOrgPermissionsRequest>, I>>(
    object: I,
  ): GetCreateOrgPermissionsRequest {
    const message = createBaseGetCreateOrgPermissionsRequest();
    message.email = object.email ?? "";
    return message;
  },
};

function createBaseGetCreateOrgPermissionsResponse(): GetCreateOrgPermissionsResponse {
  return { allowed: false };
}

export const GetCreateOrgPermissionsResponse: MessageFns<GetCreateOrgPermissionsResponse> = {
  encode(message: GetCreateOrgPermissionsResponse, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.allowed !== false) {
      writer.uint32(8).bool(message.allowed);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): GetCreateOrgPermissionsResponse {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseGetCreateOrgPermissionsResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 8) {
            break;
          }

          message.allowed = reader.bool();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): GetCreateOrgPermissionsResponse {
    return { allowed: isSet(object.allowed) ? globalThis.Boolean(object.allowed) : false };
  },

  toJSON(message: GetCreateOrgPermissionsResponse): unknown {
    const obj: any = {};
    if (message.allowed !== false) {
      obj.allowed = message.allowed;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<GetCreateOrgPermissionsResponse>, I>>(base?: I): GetCreateOrgPermissionsResponse {
    return GetCreateOrgPermissionsResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<GetCreateOrgPermissionsResponse>, I>>(
    object: I,
  ): GetCreateOrgPermissionsResponse {
    const message = createBaseGetCreateOrgPermissionsResponse();
    message.allowed = object.allowed ?? false;
    return message;
  },
};

function createBaseUpdateUserPreferencesRequest(): UpdateUserPreferencesRequest {
  return { email: "", provider: "", userPreferences: undefined };
}

export const UpdateUserPreferencesRequest: MessageFns<UpdateUserPreferencesRequest> = {
  encode(message: UpdateUserPreferencesRequest, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.email !== "") {
      writer.uint32(10).string(message.email);
    }
    if (message.provider !== "") {
      writer.uint32(18).string(message.provider);
    }
    if (message.userPreferences !== undefined) {
      UserPreferences.encode(message.userPreferences, writer.uint32(90).fork()).join();
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): UpdateUserPreferencesRequest {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseUpdateUserPreferencesRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.email = reader.string();
          continue;
        }
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.provider = reader.string();
          continue;
        }
        case 11: {
          if (tag !== 90) {
            break;
          }

          message.userPreferences = UserPreferences.decode(reader, reader.uint32());
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): UpdateUserPreferencesRequest {
    return {
      email: isSet(object.email) ? globalThis.String(object.email) : "",
      provider: isSet(object.provider) ? globalThis.String(object.provider) : "",
      userPreferences: isSet(object.userPreferences) ? UserPreferences.fromJSON(object.userPreferences) : undefined,
    };
  },

  toJSON(message: UpdateUserPreferencesRequest): unknown {
    const obj: any = {};
    if (message.email !== "") {
      obj.email = message.email;
    }
    if (message.provider !== "") {
      obj.provider = message.provider;
    }
    if (message.userPreferences !== undefined) {
      obj.userPreferences = UserPreferences.toJSON(message.userPreferences);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<UpdateUserPreferencesRequest>, I>>(base?: I): UpdateUserPreferencesRequest {
    return UpdateUserPreferencesRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<UpdateUserPreferencesRequest>, I>>(object: I): UpdateUserPreferencesRequest {
    const message = createBaseUpdateUserPreferencesRequest();
    message.email = object.email ?? "";
    message.provider = object.provider ?? "";
    message.userPreferences = (object.userPreferences !== undefined && object.userPreferences !== null)
      ? UserPreferences.fromPartial(object.userPreferences)
      : undefined;
    return message;
  },
};

function createBaseUserSessionResponse(): UserSessionResponse {
  return {
    orgId: "",
    orgName: "",
    tenantId: "",
    tenantName: "",
    email: "",
    provider: "",
    name: "",
    newUser: false,
    expiry: undefined,
    loginTime: undefined,
    requireSignUp: false,
    sessionId: "",
    userId: "",
    role: 0,
    labels: [],
    cuiTenantId: "",
    federatedUser: false,
    uiVersion: "",
    uiDeploymentTimestamp: "",
    isC3Crosslaunch: false,
    c3Context: 0,
    signUpRequirement: 0,
    developer: false,
  };
}

export const UserSessionResponse: MessageFns<UserSessionResponse> = {
  encode(message: UserSessionResponse, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.orgId !== "") {
      writer.uint32(10).string(message.orgId);
    }
    if (message.orgName !== "") {
      writer.uint32(18).string(message.orgName);
    }
    if (message.tenantId !== "") {
      writer.uint32(26).string(message.tenantId);
    }
    if (message.tenantName !== "") {
      writer.uint32(34).string(message.tenantName);
    }
    if (message.email !== "") {
      writer.uint32(42).string(message.email);
    }
    if (message.provider !== "") {
      writer.uint32(50).string(message.provider);
    }
    if (message.name !== "") {
      writer.uint32(58).string(message.name);
    }
    if (message.newUser !== false) {
      writer.uint32(64).bool(message.newUser);
    }
    if (message.expiry !== undefined) {
      Timestamp.encode(toTimestamp(message.expiry), writer.uint32(74).fork()).join();
    }
    if (message.loginTime !== undefined) {
      Timestamp.encode(toTimestamp(message.loginTime), writer.uint32(82).fork()).join();
    }
    if (message.requireSignUp !== false) {
      writer.uint32(88).bool(message.requireSignUp);
    }
    if (message.sessionId !== "") {
      writer.uint32(98).string(message.sessionId);
    }
    if (message.userId !== "") {
      writer.uint32(106).string(message.userId);
    }
    if (message.role !== 0) {
      writer.uint32(112).int32(message.role);
    }
    for (const v of message.labels) {
      writer.uint32(122).string(v!);
    }
    if (message.cuiTenantId !== "") {
      writer.uint32(130).string(message.cuiTenantId);
    }
    if (message.federatedUser !== false) {
      writer.uint32(136).bool(message.federatedUser);
    }
    if (message.uiVersion !== "") {
      writer.uint32(146).string(message.uiVersion);
    }
    if (message.uiDeploymentTimestamp !== "") {
      writer.uint32(154).string(message.uiDeploymentTimestamp);
    }
    if (message.isC3Crosslaunch !== false) {
      writer.uint32(160).bool(message.isC3Crosslaunch);
    }
    if (message.c3Context !== 0) {
      writer.uint32(168).int32(message.c3Context);
    }
    if (message.signUpRequirement !== 0) {
      writer.uint32(176).int32(message.signUpRequirement);
    }
    if (message.developer !== false) {
      writer.uint32(184).bool(message.developer);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): UserSessionResponse {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseUserSessionResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.orgId = reader.string();
          continue;
        }
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.orgName = reader.string();
          continue;
        }
        case 3: {
          if (tag !== 26) {
            break;
          }

          message.tenantId = reader.string();
          continue;
        }
        case 4: {
          if (tag !== 34) {
            break;
          }

          message.tenantName = reader.string();
          continue;
        }
        case 5: {
          if (tag !== 42) {
            break;
          }

          message.email = reader.string();
          continue;
        }
        case 6: {
          if (tag !== 50) {
            break;
          }

          message.provider = reader.string();
          continue;
        }
        case 7: {
          if (tag !== 58) {
            break;
          }

          message.name = reader.string();
          continue;
        }
        case 8: {
          if (tag !== 64) {
            break;
          }

          message.newUser = reader.bool();
          continue;
        }
        case 9: {
          if (tag !== 74) {
            break;
          }

          message.expiry = fromTimestamp(Timestamp.decode(reader, reader.uint32()));
          continue;
        }
        case 10: {
          if (tag !== 82) {
            break;
          }

          message.loginTime = fromTimestamp(Timestamp.decode(reader, reader.uint32()));
          continue;
        }
        case 11: {
          if (tag !== 88) {
            break;
          }

          message.requireSignUp = reader.bool();
          continue;
        }
        case 12: {
          if (tag !== 98) {
            break;
          }

          message.sessionId = reader.string();
          continue;
        }
        case 13: {
          if (tag !== 106) {
            break;
          }

          message.userId = reader.string();
          continue;
        }
        case 14: {
          if (tag !== 112) {
            break;
          }

          message.role = reader.int32() as any;
          continue;
        }
        case 15: {
          if (tag !== 122) {
            break;
          }

          message.labels.push(reader.string());
          continue;
        }
        case 16: {
          if (tag !== 130) {
            break;
          }

          message.cuiTenantId = reader.string();
          continue;
        }
        case 17: {
          if (tag !== 136) {
            break;
          }

          message.federatedUser = reader.bool();
          continue;
        }
        case 18: {
          if (tag !== 146) {
            break;
          }

          message.uiVersion = reader.string();
          continue;
        }
        case 19: {
          if (tag !== 154) {
            break;
          }

          message.uiDeploymentTimestamp = reader.string();
          continue;
        }
        case 20: {
          if (tag !== 160) {
            break;
          }

          message.isC3Crosslaunch = reader.bool();
          continue;
        }
        case 21: {
          if (tag !== 168) {
            break;
          }

          message.c3Context = reader.int32() as any;
          continue;
        }
        case 22: {
          if (tag !== 176) {
            break;
          }

          message.signUpRequirement = reader.int32() as any;
          continue;
        }
        case 23: {
          if (tag !== 184) {
            break;
          }

          message.developer = reader.bool();
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): UserSessionResponse {
    return {
      orgId: isSet(object.orgId) ? globalThis.String(object.orgId) : "",
      orgName: isSet(object.orgName) ? globalThis.String(object.orgName) : "",
      tenantId: isSet(object.tenantId) ? globalThis.String(object.tenantId) : "",
      tenantName: isSet(object.tenantName) ? globalThis.String(object.tenantName) : "",
      email: isSet(object.email) ? globalThis.String(object.email) : "",
      provider: isSet(object.provider) ? globalThis.String(object.provider) : "",
      name: isSet(object.name) ? globalThis.String(object.name) : "",
      newUser: isSet(object.newUser) ? globalThis.Boolean(object.newUser) : false,
      expiry: isSet(object.expiry) ? fromJsonTimestamp(object.expiry) : undefined,
      loginTime: isSet(object.loginTime) ? fromJsonTimestamp(object.loginTime) : undefined,
      requireSignUp: isSet(object.requireSignUp) ? globalThis.Boolean(object.requireSignUp) : false,
      sessionId: isSet(object.sessionId) ? globalThis.String(object.sessionId) : "",
      userId: isSet(object.userId) ? globalThis.String(object.userId) : "",
      role: isSet(object.role) ? userRoleFromJSON(object.role) : 0,
      labels: globalThis.Array.isArray(object?.labels) ? object.labels.map((e: any) => globalThis.String(e)) : [],
      cuiTenantId: isSet(object.cuiTenantId) ? globalThis.String(object.cuiTenantId) : "",
      federatedUser: isSet(object.federatedUser) ? globalThis.Boolean(object.federatedUser) : false,
      uiVersion: isSet(object.uiVersion) ? globalThis.String(object.uiVersion) : "",
      uiDeploymentTimestamp: isSet(object.uiDeploymentTimestamp) ? globalThis.String(object.uiDeploymentTimestamp) : "",
      isC3Crosslaunch: isSet(object.isC3Crosslaunch) ? globalThis.Boolean(object.isC3Crosslaunch) : false,
      c3Context: isSet(object.c3Context) ? c3ContextFromJSON(object.c3Context) : 0,
      signUpRequirement: isSet(object.signUpRequirement) ? signUpRequirementFromJSON(object.signUpRequirement) : 0,
      developer: isSet(object.developer) ? globalThis.Boolean(object.developer) : false,
    };
  },

  toJSON(message: UserSessionResponse): unknown {
    const obj: any = {};
    if (message.orgId !== "") {
      obj.orgId = message.orgId;
    }
    if (message.orgName !== "") {
      obj.orgName = message.orgName;
    }
    if (message.tenantId !== "") {
      obj.tenantId = message.tenantId;
    }
    if (message.tenantName !== "") {
      obj.tenantName = message.tenantName;
    }
    if (message.email !== "") {
      obj.email = message.email;
    }
    if (message.provider !== "") {
      obj.provider = message.provider;
    }
    if (message.name !== "") {
      obj.name = message.name;
    }
    if (message.newUser !== false) {
      obj.newUser = message.newUser;
    }
    if (message.expiry !== undefined) {
      obj.expiry = message.expiry.toISOString();
    }
    if (message.loginTime !== undefined) {
      obj.loginTime = message.loginTime.toISOString();
    }
    if (message.requireSignUp !== false) {
      obj.requireSignUp = message.requireSignUp;
    }
    if (message.sessionId !== "") {
      obj.sessionId = message.sessionId;
    }
    if (message.userId !== "") {
      obj.userId = message.userId;
    }
    if (message.role !== 0) {
      obj.role = userRoleToJSON(message.role);
    }
    if (message.labels?.length) {
      obj.labels = message.labels;
    }
    if (message.cuiTenantId !== "") {
      obj.cuiTenantId = message.cuiTenantId;
    }
    if (message.federatedUser !== false) {
      obj.federatedUser = message.federatedUser;
    }
    if (message.uiVersion !== "") {
      obj.uiVersion = message.uiVersion;
    }
    if (message.uiDeploymentTimestamp !== "") {
      obj.uiDeploymentTimestamp = message.uiDeploymentTimestamp;
    }
    if (message.isC3Crosslaunch !== false) {
      obj.isC3Crosslaunch = message.isC3Crosslaunch;
    }
    if (message.c3Context !== 0) {
      obj.c3Context = c3ContextToJSON(message.c3Context);
    }
    if (message.signUpRequirement !== 0) {
      obj.signUpRequirement = signUpRequirementToJSON(message.signUpRequirement);
    }
    if (message.developer !== false) {
      obj.developer = message.developer;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<UserSessionResponse>, I>>(base?: I): UserSessionResponse {
    return UserSessionResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<UserSessionResponse>, I>>(object: I): UserSessionResponse {
    const message = createBaseUserSessionResponse();
    message.orgId = object.orgId ?? "";
    message.orgName = object.orgName ?? "";
    message.tenantId = object.tenantId ?? "";
    message.tenantName = object.tenantName ?? "";
    message.email = object.email ?? "";
    message.provider = object.provider ?? "";
    message.name = object.name ?? "";
    message.newUser = object.newUser ?? false;
    message.expiry = object.expiry ?? undefined;
    message.loginTime = object.loginTime ?? undefined;
    message.requireSignUp = object.requireSignUp ?? false;
    message.sessionId = object.sessionId ?? "";
    message.userId = object.userId ?? "";
    message.role = object.role ?? 0;
    message.labels = object.labels?.map((e) => e) || [];
    message.cuiTenantId = object.cuiTenantId ?? "";
    message.federatedUser = object.federatedUser ?? false;
    message.uiVersion = object.uiVersion ?? "";
    message.uiDeploymentTimestamp = object.uiDeploymentTimestamp ?? "";
    message.isC3Crosslaunch = object.isC3Crosslaunch ?? false;
    message.c3Context = object.c3Context ?? 0;
    message.signUpRequirement = object.signUpRequirement ?? 0;
    message.developer = object.developer ?? false;
    return message;
  },
};

type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;

export type DeepPartial<T> = T extends Builtin ? T
  : T extends globalThis.Array<infer U> ? globalThis.Array<DeepPartial<U>>
  : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>>
  : T extends {} ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P
  : P & { [K in keyof P]: Exact<P[K], I[K]> } & { [K in Exclude<keyof I, KeysOfUnion<P>>]: never };

function toTimestamp(date: Date): Timestamp {
  const seconds = Math.trunc(date.getTime() / 1_000).toString();
  const nanos = (date.getTime() % 1_000) * 1_000_000;
  return { seconds, nanos };
}

function fromTimestamp(t: Timestamp): Date {
  let millis = (globalThis.Number(t.seconds) || 0) * 1_000;
  millis += (t.nanos || 0) / 1_000_000;
  return new globalThis.Date(millis);
}

function fromJsonTimestamp(o: any): Date {
  if (o instanceof globalThis.Date) {
    return o;
  } else if (typeof o === "string") {
    return new globalThis.Date(o);
  } else {
    return fromTimestamp(Timestamp.fromJSON(o));
  }
}

function isSet(value: any): boolean {
  return value !== null && value !== undefined;
}

export interface MessageFns<T> {
  encode(message: T, writer?: BinaryWriter): BinaryWriter;
  decode(input: BinaryReader | Uint8Array, length?: number): T;
  fromJSON(object: any): T;
  toJSON(message: T): unknown;
  create<I extends Exact<DeepPartial<T>, I>>(base?: I): T;
  fromPartial<I extends Exact<DeepPartial<T>, I>>(object: I): T;
}
